import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  User as FirebaseUser,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../../../config/firebase';
import { useAuthStore } from '../store/authStore';

class AuthService {

  refreshInterval: NodeJS.Timeout | null = null;

  private waitForAuthUser(): Promise<FirebaseUser | null> {
    return new Promise((resolve) => {
      let settled = false;
      let unsubscribe: () => void = () => { };
      const timeoutId = setTimeout(() => {
        if (!settled) {
          settled = true;
          unsubscribe();
          resolve(null);
        }
      }, 3000);

      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser && !settled) {
          settled = true;
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(firebaseUser);
        }
      });
    });
  }

  async getCurrentUser() {
    return auth.currentUser;
  }

  async loginWithEmail(email: string, password: string) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  async registerWithEmail(email: string, password: string) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  async updateProfile(displayName: string) {
    if (!auth.currentUser) {
      throw new Error('No authenticated user found');
    }
    await updateProfile(auth.currentUser, { displayName });
  }

  async getToken(forceRefresh: boolean = false) {
    let user = auth.currentUser;
    if (!user) {
      user = await this.waitForAuthUser();
    }
    if (user) {
      return user.getIdToken(forceRefresh);
    }
    return null;
  }

  /**
   * Get token synchronously (for interceptors)
   * This is a workaround since interceptors can't handle async functions well
   */
  getTokenSync() {
    const { token } = useAuthStore.getState();
    return token;
  }

  /**
   * Refresh the authentication token
   * @returns {Promise<string>} The new token
   */
  async refreshAuthToken(): Promise<string> {
    try {
      const token = await this.getToken(true)
      if (token) {
        // Update token in auth store
        const authStore = useAuthStore.getState();
        authStore.setToken(token);
        return token;
      }
      throw new Error('Failed to refresh token');
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  /**
   * Start token refresh interval
   * Refreshes the token every 45 minutes to ensure it never expires
   * Firebase tokens typically expire after 1 hour
   */
  startTokenRefresh() {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // First refresh immediately to ensure we have a fresh token
    this.refreshAuthToken().catch(error => {
      console.error('Initial token refresh failed:', error);
    });

    // Set up new interval - refresh every 45 minutes
    // This ensures token is always fresh even if page is reloaded
    this.refreshInterval = setInterval(async () => {
      try {
        const token = await this.getToken(true); // Force token refresh
        if (token) {
          // Update token in auth store
          const authStore = useAuthStore.getState();
          authStore.setToken(token);
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
        // If there's an error refreshing the token, stop the interval
        this.stopTokenRefresh();
      }
    }, 45 * 60 * 1000); // 45 minutes
  }

  /**
   * Stop token refresh interval
   */
  stopTokenRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Handle complete logout process including Firebase and local state
   * @returns {Promise<void>}
   */
  async handleLogout(redirectToSignin: boolean = true): Promise<void> {
    try {
      // Sign out from Firebase
      await signOut(auth);

      // Clear authentication state from Zustand store
      const authStore = useAuthStore.getState();
      authStore.logout();

      // Clear any stored data or cookies
      localStorage.removeItem('user-settings');
      sessionStorage.clear();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  /**
   * Handle session expiration or invalid token
   * @returns {Promise<void>}
   */
  async handleSessionExpired(): Promise<void> {
    await this.handleLogout();
    return Promise.reject(new Error('Session expired. Please login again.'));
  }

  // Reset password methods
  async sendPasswordResetEmail(email) {
    await sendPasswordResetEmail(auth, email);
  }

  async confirmPasswordReset(oobCode, newPassword) {
    await confirmPasswordReset(auth, oobCode, newPassword);
  }

  /**
   * Check if the current token is valid
   * @returns {boolean}
   */
  isValidToken(): boolean {
    const token = useAuthStore.getState().token;
    if (!token) return false;

    try {
      return true;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();
